import { parse } from '@de/utils/interpolation';
import type { DataSourceDeclaration } from '@de/providers/DataSourceManager.types';
import { getDeclarationInterpolatableFields } from '@de/utils/data-sources/data-source-kind';
import type { TopologyProjection } from './project-canvas-topology';

/**
 * A declaration read `topology.siblings.<id>` and that id is not projected.
 *
 * Interpolation would otherwise turn the missing path into `undefined`
 * (or `''` inside a mixed template). Callers surface this on the source
 * instead of fetching with an empty stand-in.
 */
export class TopologyBlockAbsentError extends Error {
  readonly blockId: string;

  constructor(blockId: string) {
    super(`Topology block "${blockId}" is not in the projection.`);
    this.name = 'TopologyBlockAbsentError';
    this.blockId = blockId;
    Object.setPrototypeOf(this, TopologyBlockAbsentError.prototype);
  }
}

/**
 * First sibling id referenced by `decl` that `projection` does not contain.
 *
 * Returns the id string. A missing block is never represented as `[]`.
 */
export function findAbsentTopologyBlockId(
  decl: DataSourceDeclaration,
  projection: TopologyProjection,
): string | undefined {
  return walk(getDeclarationInterpolatableFields(decl), projection);
}

function walk(
  value: unknown,
  projection: TopologyProjection,
): string | undefined {
  if (typeof value === 'string') {
    return absentInTemplate(value, projection);
  }
  if (Array.isArray(value)) {
    for (const item of value) {
      const found = walk(item, projection);
      if (found !== undefined) return found;
    }
    return undefined;
  }
  if (value !== null && typeof value === 'object') {
    for (const item of Object.values(value as Record<string, unknown>)) {
      const found = walk(item, projection);
      if (found !== undefined) return found;
    }
  }
  return undefined;
}

function absentInTemplate(
  template: string,
  projection: TopologyProjection,
): string | undefined {
  if (!template.includes('{{')) return undefined;
  let parsed;
  try {
    parsed = parse(template);
  } catch {
    return undefined;
  }
  for (const segment of parsed.segments) {
    if (segment.type !== 'expression' || segment.kind !== 'path') continue;
    const [root, siblings, id] = segment.segments;
    if (root?.type !== 'key' || root.name !== 'topology') continue;
    if (siblings?.type !== 'key' || siblings.name !== 'siblings') continue;
    if (id?.type !== 'key') continue;
    if (!Object.prototype.hasOwnProperty.call(projection.siblings, id.name)) {
      return id.name;
    }
  }
  return undefined;
}
